import React, { useEffect, useState } from 'react';

import { WORKFLOW_CHART_DIMENSIONS, WORKFLOW_LIST_GROUPABLE_DIMENSIONS } from '@aline/common';
import type { WorkflowListGroupableDimension } from '@aline/common';
import type { SelectChangeEvent } from '@mui/material';
import { Badge, FormControl, Menu, MenuItem, Select, Typography } from '@mui/material';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import { Group } from 'lucide-react';
import { DropdownSelectButton } from 'src/components';
import { makeStyles } from 'tss-react/mui';

/**
 * Menu labels follow the list's column headers, which read differently from the
 * shared Dimension labels ('Status' for the column Dimension, for one).
 */
const GROUPING_OPTION_LABELS = {
  [WORKFLOW_CHART_DIMENSIONS.assignee]: 'Assignee',
  [WORKFLOW_CHART_DIMENSIONS.board]: 'Board',
  [WORKFLOW_CHART_DIMENSIONS.column]: 'Status',
  [WORKFLOW_CHART_DIMENSIONS.columnCategory]: 'Status Type',
  [WORKFLOW_CHART_DIMENSIONS.owner]: 'Requester',
  [WORKFLOW_CHART_DIMENSIONS.priority]: 'Priority',
  [WORKFLOW_CHART_DIMENSIONS.state]: 'Signing Status',
  [WORKFLOW_CHART_DIMENSIONS.workflowType]: 'Workflow type',
} as const satisfies Record<WorkflowListGroupableDimension, string>;

const GROUPING_OPTIONS = WORKFLOW_LIST_GROUPABLE_DIMENSIONS.map(field => ({
  field,
  label: GROUPING_OPTION_LABELS[field],
}));

const useStyles = makeStyles()(theme => ({
  badge: {
    '& .MuiBadge-badge': {
      right: 2,
      top: 2,
    },
  },
  formControl: {
    display: 'flex',
    gap: theme.spacing(1),
    margin: theme.spacing(1, 2),
    minWidth: 260,
  },
  formRow: {
    alignItems: 'center',
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: '90px minmax(140px, 1fr)',
  },
}));

export function WorkflowGroupingMenu({
  listApiRef,
}: {
  listApiRef: React.RefObject<GridApiPremium | null> | null;
}): JSX.Element | null {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [rowGroupingModel, setRowGroupingModel] = useState<string[]>([]);

  useEffect(() => {
    const gridApi = listApiRef?.current;
    if (!gridApi) return;
    setRowGroupingModel(gridApi.state.rowGrouping?.model ?? []);
    return gridApi.subscribeEvent('rowGroupingModelChange', setRowGroupingModel);
  }, [listApiRef]);

  if (!listApiRef) return null;

  const groupingField = rowGroupingModel[0] ?? '';
  const subgroupingField = rowGroupingModel[1] ?? '';

  const handleGroupingChange = (event: SelectChangeEvent) => {
    const field = event.target.value;
    const nextModel = field ? [field] : [];
    if (field && subgroupingField && subgroupingField !== field) {
      nextModel.push(subgroupingField);
    }
    listApiRef.current?.setRowGroupingModel(nextModel);
  };

  const handleSubgroupingChange = (event: SelectChangeEvent) => {
    const field = event.target.value;
    listApiRef.current?.setRowGroupingModel(
      field && groupingField ? [groupingField, field] : groupingField ? [groupingField] : []
    );
  };

  return (
    <>
      <Badge
        badgeContent={rowGroupingModel.length || null}
        className={classes.badge}
        color="primary"
      >
        <DropdownSelectButton
          ref={setAnchorEl}
          buttonVariant="default"
          icon={<Group size={16} />}
          label="Group"
          selected={menuOpen}
          variant="outlined"
          onClick={() => setMenuOpen(open => !open)}
        />
      </Badge>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => setMenuOpen(false)}
      >
        <FormControl className={classes.formControl} size="small">
          <div className={classes.formRow}>
            <Typography variant="subtitle2">Group by</Typography>
            <Select
              displayEmpty
              fullWidth
              inputProps={{ 'aria-label': 'Group by' }}
              value={groupingField}
              onChange={handleGroupingChange}
            >
              <MenuItem value="">None</MenuItem>
              {GROUPING_OPTIONS.map(option => (
                <MenuItem key={option.field} value={option.field}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className={classes.formRow}>
            <Typography variant="subtitle2">Then by</Typography>
            <Select
              displayEmpty
              fullWidth
              disabled={!groupingField}
              inputProps={{ 'aria-label': 'Then group by' }}
              value={subgroupingField}
              onChange={handleSubgroupingChange}
            >
              <MenuItem value="">None</MenuItem>
              {GROUPING_OPTIONS.filter(option => option.field !== groupingField).map(option => (
                <MenuItem key={option.field} value={option.field}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </FormControl>
      </Menu>
    </>
  );
}
